/**
 * Cardo — Shared TypeScript Types
 *
 * These mirror the Pydantic models in the backend exactly.
 * Single source of truth for the frontend.
 */

export type RiskLevel = "low" | "moderate" | "high" | "critical";

export type SchedulePhase =
  | "sitework_excavation"
  | "steel_erection"
  | "roofing"
  | "concrete_formwork"
  | "demolition"
  | "mechanical_electrical"
  | "exterior_envelope"
  | "interior_finishes"
  | "commissioning"
  | "mobilization";

export interface PhaseInfo {
  value: SchedulePhase;
  label: string;
  baseRisk: number;
}

export const PHASES: PhaseInfo[] = [
  { value: "roofing", label: "Roofing", baseRisk: 95 },
  { value: "steel_erection", label: "Steel Erection", baseRisk: 90 },
  { value: "demolition", label: "Demolition", baseRisk: 88 },
  { value: "sitework_excavation", label: "Sitework / Excavation", baseRisk: 85 },
  { value: "concrete_formwork", label: "Concrete Formwork", baseRisk: 80 },
  { value: "exterior_envelope", label: "Exterior Envelope", baseRisk: 75 },
  { value: "mechanical_electrical", label: "Mechanical / Electrical", baseRisk: 70 },
  { value: "commissioning", label: "Commissioning", baseRisk: 45 },
  { value: "interior_finishes", label: "Interior Finishes", baseRisk: 35 },
  { value: "mobilization", label: "Mobilization", baseRisk: 25 },
];

export interface WeatherData {
  temperature_f: number;
  wind_speed_mph: number;
  wind_gust_mph: number | null;
  precipitation_probability: number;
  visibility_miles: number;
  lightning_risk: boolean;
}

export interface WorkforceData {
  total_workers: number;
  new_workers_under_90_days: number;
  trades_on_site: number;
  overtime_hours_this_week: number;
  night_shift: boolean;
}

export interface ComplianceData {
  open_violations: number;
  violations_last_7_days: number;
  violations_last_30_days: number;
  ppe_compliance_rate: number;
  days_since_last_incident: number;
  toolbox_talk_completed_today: boolean;
}

export interface EquipmentData {
  cranes_active: number;
  heavy_equipment_count: number;
  man_lifts_active: number;
  concrete_pumps_active: number;
  critical_lifts_planned: boolean;
}

export interface SiteProfile {
  project_id: string;
  project_name: string;
  location_name: string;
  latitude: number;
  longitude: number;
  schedule_phases: SchedulePhase[];
  concurrent: number;
  pressure: number;
  workforce: WorkforceData;
  compliance: ComplianceData;
  equipment: EquipmentData;
}

export interface Recommendation {
  priority: number;
  category: string;
  message: string;
  action: string;
}

export interface ScoreResult {
  overall: number;
  level: RiskLevel;
  scores: Record<string, number>;
  recs: Recommendation[];
  sif: string[];
}

/** Labels for component score keys */
export const COMPONENT_LABELS: Record<string, string> = {
  schedule_phase: "Schedule",
  weather_environmental: "Weather",
  compliance: "Compliance",
  workforce: "Workforce",
  historical: "Historical",
  equipment: "Equipment",
  temporal: "Temporal",
};
